import Capitalize from '@stdlib/string-capitalize'

import { Broadcast, Comments, Copy, DeleteFive, Edit, FileText, History, Peoples, Plus } from '@icon-park/react'

import { MentionResponse } from '@shared/models/mentions'

import { Tokens } from '@/assets/tokens'

import { copyToClipboard } from '@/utils/clipboard'
import { formatNumber } from '@/utils/numbers'
import { Str, truncateString, sanitizePublicationHtml } from '@/utils/strings'

import { ActionMenuItem, ActionsMenu, Button, Canal, Divider, Flex, Switch, Tooltip } from '@/components'
import { SkeletonLoader } from '@/components/derived'
import { Layout } from '@/components/layout'

import { Filters } from './components/Filters/Filters.component'
import { PolarityDropdown } from './components/Polarity/Polarity.component'
import { Role } from './components/Role/Role'
import { appendAtIfNeeded, getChannelDisplayText, getPotentialReach, PRESS_SOURCE_ID } from './utils'

import { FlexTable, FlexTableColumnType } from '@/components/derived/FlexTable/FlexTable.component'
import { PageTitle } from '@/components/derived/PageTitle'
import { Env, Routes } from '@/configs'
import { SortOptions } from '@/configs/sortOptions'
import { useRouting } from '@/hooks'
import { Alert, Auth, Confirmation } from '@/modules'
import { useCallback, useMemo, useState } from 'react'
import { CompanyDetailsContextProvider, useCompanyContext } from '../CompanyDetails/CompanyDetails.context'
import { MentionsContextProvider, RemovableMentionTag, useMentionsContext } from './Mentions.context'
import {
  ContentContainer,
  ContentText,
  DateContainer,
  MentionsContainer,
  MentionsTab,
  MentionsTabsContainer,
  TagItem,
  TagRemoveButton,
  ThemesGroup,
  ThemesRowContainer,
} from './Mentions.styles'
import { BatchEditModal } from './components/BatchEditModal/BatchEditModal.component'
import { ContentAlerts } from './components/ContentAlerts/ContentAlerts.component'
import { Dashboard } from './components/Dashboard/Dashboard.component'
import { EditModal } from './components/EditModal/EditModal.component'
import { EngagementRow } from './components/Engagement/Engagement.component'
import { HistoryPopover } from './components/HistoryPopover/HistoryPopover.component'
import { LinkBrandsModal } from './components/LinkBrandsModal/LinkBrandsModal.component'
import { MentionDetailModal } from './components/MentionDetailModal/MentionDetailModal.component'
import { UserResponseRole } from '@shared/models'

export function Mentions() {
  return (
    <CompanyDetailsContextProvider>
      <MentionsContextProvider>
        <MentionsComponent />
      </MentionsContextProvider>
    </CompanyDetailsContextProvider>
  )
}

function MentionsComponent() {
  const {
    isLoadingMentions,
    images,
    mentions,
    mentionModalType,
    selectMention,
    selectedMentionIds,
    selectAllVisible,
    toggleMentionSelection,
    toggleSelectAllVisible,
    batchDeleteMentions,
    deleteAllMentions,
    mentionsIdList,
    isDeletingBatch,
    currentPageSize,
    currentSort,
    setCurrentPageSize,
    setCurrentSort,
    updateMentionPolarity,
    removeMentionTag,
    isTagPending,
    setMentionReviewed,
    isReviewPending,
    expandedId,
    setExpandedId,
  } = useMentionsContext()

  const { companyID, navigate, changeQueryParam } = useRouting()
  const { company } = useCompanyContext()

  const [showBatchEditModal, setShowBatchEditModal] = useState(false)
  const [showLinkBrandsModal, setShowLinkBrandsModal] = useState(false)
  const [activeTab, setActiveTab] = useState<'data' | 'dashboard'>('data')

  // Exclusões grandes pedem digitar o nome da empresa pra confirmar — fricção
  // proporcional ao tamanho do estrago possível (incidente de 2026-07-29).
  const LARGE_DELETE_CONFIRMATION_THRESHOLD = 500

  const { open: openDeleteConfirmation } = Confirmation.Hook({
    icon: DeleteFive,
    text: `Esta ação irá excluir permanentemente as ${selectedMentionIds?.size ?? 0} postagens selecionadas. Tem certeza que deseja apagar as postagens?`,
    confirmText: 'Sim! Apagar postagens',
    requiredConfirmationText:
      (selectedMentionIds?.size ?? 0) > LARGE_DELETE_CONFIRMATION_THRESHOLD ? company?.name : undefined,
  })

  const { open: openDeleteAllConfirmation } = Confirmation.Hook({
    icon: DeleteFive,
    text: `Esta ação irá excluir permanentemente as ${mentionsIdList?.length ?? 0} postagens resgatadas a partir a sua busca! Tem certeza que deseja apagar as postagens?`,
    confirmText: 'Sim! Apagar postagens',
    requiredConfirmationText:
      (mentionsIdList?.length ?? 0) > LARGE_DELETE_CONFIRMATION_THRESHOLD ? company?.name : undefined,
  })

  const currMentions = useMemo(() => {
    if (!mentions?.elements) return []

    const uniquePostIds = new Set(mentions.elements.map((m) => `${m.postID}-${m.brandID}`))

    if (mentions.elements.length !== uniquePostIds.size) {
      return mentions.elements.filter(
        (mention, index, array) =>
          index === array.findIndex((m) => m.postID === mention.postID && m.brandID === mention.brandID),
      )
    }

    return mentions.elements
  }, [mentions?.elements])

  const { sendAlert } = Alert.Hook()

  const createMentionActions = useCallback(
    (mention: MentionResponse, index: number): ActionMenuItem[] => {
      const handleCopy = async () => {
        const mentionInfo = `ID da Menção: ${mention.postID}\nMarca: ${mention.brand} (ID: ${mention.brandID})`
        await copyToClipboard(mentionInfo)
        sendAlert({ type: 'success', title: 'Copiar ID post', message: 'Copiado com sucesso!' })
      }

      return [
        {
          key: 'edit',
          label: 'Editar menção',
          icon: <Edit size={16} />,
          onClick: () => selectMention({ idx: index, val: mention }, 'edit'),
        },
        {
          key: 'detail',
          label: 'Ver detalhes',
          icon: <FileText size={16} />,
          onClick: () => selectMention({ idx: index, val: mention }, 'detail'),
        },
        {
          key: 'copy',
          label: 'Copiar ID post',
          icon: <Copy size={16} />,
          onClick: handleCopy,
        },
      ]
    },
    [selectMention, copyToClipboard],
  )

  const handleBatchDelete = useCallback(() => {
    openDeleteConfirmation(batchDeleteMentions)
  }, [openDeleteConfirmation, batchDeleteMentions])

  const handleDeleteAll = useCallback(() => {
    openDeleteAllConfirmation(deleteAllMentions)
  }, [openDeleteAllConfirmation, deleteAllMentions])

  const handleSortChange = useCallback(
    (sort: { key: string; direction: 'ASC' | 'DESC' }) => {
      setCurrentSort(sort)
    },
    [setCurrentSort],
  )

  const handlePageSizeChange = useCallback(
    (size: number) => {
      setCurrentPageSize(size)
      changeQueryParam({
        param: 'pagina',
        value: '1',
      })
    },
    [setCurrentPageSize, changeQueryParam],
  )

  const parseDateString = useCallback((dateStr: string) => {
    const [year, month, day] = dateStr.split('-').map(Number)
    return new Date(year, month - 1, day)
  }, [])

  const renderMentionMetrics = useCallback((mentionItem: MentionResponse) => {
    const engagementData = {
      likes: mentionItem.likes,
      comments: mentionItem.comments,
      views: mentionItem.views,
      reactions: mentionItem.reactions,
      shares: mentionItem.shares,
      totalInteractions: mentionItem.totalInteractions,
    }

    return (
      <Flex $type="row" $align="center" style={{ width: '100%' }}>
        <Flex $type="column" $justify="space-between" $align="center" style={{ minWidth: '80px' }}>
          <ContentAlerts
            hasNoContent={mentionItem.hasNoContent}
            hasTitleEqualsContent={mentionItem.hasTitleEqualsContent}
          />
        </Flex>
        <Flex $type="column" $align="flex-start" style={{ width: '100%', paddingLeft: '16px' }}>
          <EngagementRow engagement={engagementData} />
        </Flex>
      </Flex>
    )
  }, [])

  const renderTagRemoveButton = useCallback(
    (mentionItem: MentionResponse, tag: RemovableMentionTag) => {
      const isPending = isTagPending(mentionItem, tag)

      return (
        <TagRemoveButton
          onClick={() => removeMentionTag(mentionItem, tag)}
          disabled={isPending}
          title={isPending ? 'Removendo…' : `Remover ${tag.name}`}>
          ×
        </TagRemoveButton>
      )
    },
    [isTagPending, removeMentionTag],
  )

  const renderMentionThemes = useCallback(
    (mentionItem: MentionResponse) => {
      const spokespersons = mentionItem.spokespersons || []
      const keyMessages = mentionItem.keyMessages || []
      const parsedDate = parseDateString(mentionItem.date.toString())
      const title = mentionItem.title
      const id = `${mentionItem.postID}_${mentionItem.brandID}`
      const isExpanded = expandedId === id

      return (
        <ThemesRowContainer>
          <DateContainer>{parsedDate.toLocaleDateString('pt-BR')}</DateContainer>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: 1 }}>
            {title !== mentionItem.publication && (
              <ContentContainer>
                {isExpanded && (
                  <ContentText dangerouslySetInnerHTML={{ __html: sanitizePublicationHtml(mentionItem.publication) }} />
                )}

                <h5
                  style={{
                    fontSize: 12,
                    color: '#2563eb',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                  }}
                  onClick={() => setExpandedId(isExpanded ? null : id)}>
                  {isExpanded ? 'Ocultar conteúdo' : 'Ver conteúdo'}
                </h5>
              </ContentContainer>
            )}

            {spokespersons.length > 0 ?
              <ThemesGroup>
                <span
                  style={{ fontSize: '13px', fontWeight: '600', color: Tokens.ColorTextSecondary, marginRight: '6px' }}>
                  Porta-voz:
                </span>
                {spokespersons.map((spokesperson) => (
                  <TagItem
                    key={`${mentionItem.postID}-spokesperson-${spokesperson.internalID}`}
                    style={{ backgroundColor: '#e3f2fd', color: '#1976d2', borderColor: '#1976d2' }}>
                    👤 {spokesperson.name}
                    {renderTagRemoveButton(mentionItem, {
                      kind: 'spokesperson',
                      internalID: spokesperson.internalID,
                      name: spokesperson.name,
                    })}
                  </TagItem>
                ))}
              </ThemesGroup>
            : <ThemesGroup>
                <span
                  style={{ fontSize: '13px', fontWeight: '600', color: Tokens.ColorTextSecondary, marginRight: '6px' }}>
                  Porta-vozes:
                </span>
                <TagItem
                  key={`${mentionItem.postID}-spokesperson-na`}
                  style={{ backgroundColor: '#e3f2fd', color: '#1976d2', borderColor: '#1976d2' }}>
                  👤 N/A
                </TagItem>
              </ThemesGroup>
            }
            {keyMessages.length > 0 && (
              <ThemesGroup>
                <span
                  style={{ fontSize: '13px', fontWeight: '600', color: Tokens.ColorTextSecondary, marginRight: '6px' }}>
                  Mensagens-chave:
                </span>
                {keyMessages.map((keyMessage) => (
                  <span
                    key={`${mentionItem.postID}-keymessage-${keyMessage.internalID}`}
                    style={{
                      backgroundColor: Tokens.ColorUIInfoLight,
                      color: Tokens.ColorUIInfoDim,
                      padding: '2px 6px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      whiteSpace: 'nowrap',
                      minHeight: '20px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}>
                    {keyMessage.name}
                    {renderTagRemoveButton(mentionItem, {
                      kind: 'keyMessage',
                      internalID: keyMessage.internalID,
                      name: keyMessage.name,
                    })}
                  </span>
                ))}
              </ThemesGroup>
            )}
            {mentionItem.themeGroup && mentionItem.themeGroup.length > 0 && (
              <ThemesGroup>
                <span
                  style={{ fontSize: '13px', fontWeight: '600', color: Tokens.ColorTextSecondary, marginRight: '6px' }}>
                  Temas:
                </span>
                {mentionItem.themeGroup.flatMap((group) =>
                  group.themes.map((theme) => (
                    <TagItem
                      key={`${mentionItem.postID}-theme-${group.internalID}-${theme.internalID}`}
                      title={`${Str.from(group.name).initCap}`}>
                      {theme.name}
                      {renderTagRemoveButton(mentionItem, {
                        kind: 'theme',
                        internalID: theme.internalID,
                        name: theme.name,
                        themeGroupInternalID: group.internalID,
                      })}
                    </TagItem>
                  )),
                )}
              </ThemesGroup>
            )}
          </div>
        </ThemesRowContainer>
      )
    },
    [parseDateString, expandedId, setExpandedId, renderTagRemoveButton],
  )

  const BatchActionsHeader = useCallback(
    () => (
      <Flex $type="row" $justify="flex-end" $align="center" $gap={4}>
        <Button.Root
          $variant="link"
          onClick={() => setShowBatchEditModal(true)}
          disabled={!selectedMentionIds || selectedMentionIds.size === 0}
          title={
            !selectedMentionIds || selectedMentionIds.size === 0 ?
              'Selecione menções para editar'
            : `Editar ${selectedMentionIds.size} menções selecionadas`
          }
          style={{
            minWidth: 'auto',
            width: '24px',
            height: '24px',
            padding: 0,
            borderRadius: '4px',
          }}>
          <Button.Icon
            value={Edit}
            color={
              !selectedMentionIds || selectedMentionIds.size === 0 ?
                Tokens.ColorTextTertiary
              : Tokens.ColorTextSecondary
            }
          />
        </Button.Root>

        <Button.Root
          $variant="link"
          onClick={handleBatchDelete}
          disabled={!selectedMentionIds || selectedMentionIds.size === 0}
          $loading={isDeletingBatch}
          title={
            !selectedMentionIds || selectedMentionIds.size === 0 ?
              'Selecione menções para excluir'
            : `Excluir ${selectedMentionIds.size} menções selecionadas`
          }
          style={{
            minWidth: 'auto',
            width: '24px',
            height: '24px',
            padding: 0,
            borderRadius: '4px',
          }}>
          <Button.Icon
            value={DeleteFive}
            color={
              !selectedMentionIds || selectedMentionIds.size === 0 ? Tokens.ColorTextTertiary : Tokens.ColorUIDanger
            }
          />
        </Button.Root>

        <Button.Root
          $variant="link"
          onClick={() => setShowLinkBrandsModal(true)}
          disabled={!selectedMentionIds || selectedMentionIds.size === 0}
          title={
            !selectedMentionIds || selectedMentionIds.size === 0 ?
              'Selecione menções para adicionar a outras marcas'
            : `Adicionar ${selectedMentionIds.size} menções selecionadas a outras marcas`
          }
          style={{
            minWidth: 'auto',
            width: '24px',
            height: '24px',
            padding: 0,
            borderRadius: '4px',
          }}>
          <Button.Icon
            value={Plus}
            color={
              !selectedMentionIds || selectedMentionIds.size === 0 ?
                Tokens.ColorTextTertiary
              : Tokens.ColorTextSecondary
            }
          />
        </Button.Root>
      </Flex>
    ),
    [selectedMentionIds, handleBatchDelete, isDeletingBatch],
  )

  const columns: FlexTableColumnType<MentionResponse>[] = useMemo(
    () => [
      {
        key: 'channel',
        title: 'Canal',
        width: '80px',
        render: (item) => (
          <Flex $type="column" $justify="center" $align="flex-start">
            <Canal
              brandImage={images?.get(item.brandLogo)}
              brandName={item.brand}
              hyperlink={item.hyperlink}
              channelImage={images?.get(item.channelLogoID)}
              channelName={getChannelDisplayText(item.sourceInternalID, item.channelName, item.approach)}
            />
          </Flex>
        ),
      },
      {
        key: 'publication',
        title: 'Publicação',
        // Única coluna flexível: sem piso, a coluna "Revisão" a esmaga na
        // largura mínima da tabela.
        minWidth: 240,
        render: (item) => {
          const publisherInfo = `${appendAtIfNeeded(item.profile)}`
          const title = item.title ? item.title : item.publication
          const titleSameAsPublication = item.publication === title
          const id = `${item.postID}_${item.brandID}`
          const isExpanded = expandedId === id
          return (
            <Flex $type="column" $justify="center" $align="flex-start" $gap={8}>
              <h4>{publisherInfo}</h4>
              <span style={{ fontWeight: isExpanded ? 'bold' : 'normal' }}>
                {isExpanded ?
                  titleSameAsPublication ?
                    ''
                  : title
                : truncateString(title, 250)}
              </span>
            </Flex>
          )
        },
      },
      {
        key: 'potentialReach',
        title: 'Alcance',
        width: '140px',
        align: 'center',
        render: (item) => {
          const publisherType = Capitalize(item.publisherType)
          const followers = item.followers ?? 0
          const isPress = item.sourceInternalID === PRESS_SOURCE_ID
          const potential = getPotentialReach(
            item.sourceInternalID,
            followers,
            Number(process.env[Env.REACT_APP_POTENCIAL_REACH_FACTOR] ?? 0.2),
          )
          const reachValue = formatNumber(potential)
          const followersFormatted = formatNumber(item.followers || 0)
          return (
            <Flex $type="column" $justify="center" $align="center">
              <Tooltip value={publisherType} length={30} />
              <div
                style={{
                  width: '70px',
                  height: '1px',
                  backgroundColor: Tokens.ColorLight,
                }}
              />
              <Flex $type="column" $justify="center" $align="center" $gap={32}>
                <Flex $type="row" $justify="center" $align="center" $gap={8}>
                  <Broadcast size={16} />
                  <span title={reachValue}>{reachValue}</span>
                </Flex>

                {!isPress && (
                  <Flex
                    $type="row"
                    $justify="center"
                    $align="center"
                    style={{
                      gap: '8px',
                      fontSize: '12px',
                      color: Tokens.ColorTextSecondary,
                      display: 'flex',
                      alignItems: 'center',
                    }}>
                    <Peoples color={Tokens.ColorTextPrimary} />
                    {followersFormatted} seguidores
                  </Flex>
                )}
              </Flex>
            </Flex>
          )
        },
      },
      {
        key: 'polarity',
        title: 'Polaridade',
        width: '160px',
        minWidth: 160,
        align: 'center',
        render: (item) => (
          <Flex $type="row" $align="center" $gap={4}>
            <PolarityDropdown
              brandID={item.brandID}
              polarity={item.polarity}
              mentionID={item.postID}
              companyID={item.companyID}
              onUpdated={(newPolarity) => {
                updateMentionPolarity(item.postID, item.brandID, newPolarity)
              }}
            />
          </Flex>
        ),
      },
      {
        key: 'role',
        title: 'Abordagem',
        align: 'center',
        width: '140px',
        minWidth: 140,
        render: (item) => {
          const roleText = item.role ? Capitalize(item.role.toLowerCase()) : '—'
          return (
            <Flex $type="column" $justify="center" $align="center">
              <Role value={item.stars} />
              <Tooltip value={roleText} />
            </Flex>
          )
        },
      },
      {
        key: 'approach',
        title: 'Formato',
        width: '150px',
        minWidth: 150,
        align: 'center',
        render: (item) => {
          const approachText = Capitalize(item.approach)
          return (
            <Flex $type="column" $justify="center" $align="center">
              <Tooltip value={approachText} />
            </Flex>
          )
        },
      },
      {
        // O toggle de revisão mora aqui dentro, e não numa coluna própria: em
        // coluna separada ele ficava lado a lado com o menu e o histórico,
        // ocupando 100px só pra isso. Empilhado, divide a mesma célula.
        key: 'actions',
        title: <BatchActionsHeader />,
        width: '108px',
        render: (item, index) => {
          // Menção só revisada tem auditoria mas hasChanges=false; sem isso o
          // "quem revisou / quando" ficaria inacessível. Desmarcar esconde o
          // ícone de novo mesmo com histórico — efeito aceito.
          const showHistory = (item.hasChanges ?? false) || item.reviewed === true
          // Switch, e não Checkbox: a linha já tem o checkbox verde de seleção
          // em lote (que inclui excluir); dois iguais convidam a clique errado.
          const hasLink = item.reviewed !== null && item.reviewed !== undefined
          const reviewed = item.reviewed ?? false
          const pending = isReviewPending(item)
          const reviewTitle =
            !hasLink ? 'Menção sem vínculo de marca; não pode ser revisada'
            : pending ? 'Salvando…'
            : reviewed ? 'Desmarcar revisão'
            : 'Marcar como revisada'
          return (
            <Flex $type="column" $align="center" $gap={4} style={{ justifyContent: 'center' }}>
              <span title={reviewTitle} style={{ display: 'inline-flex' }}>
                <Switch.Root
                  isChecked={reviewed}
                  toggle={() => setMentionReviewed(item, !reviewed)}
                  disabled={!hasLink || pending}
                />
              </span>
              <Flex $type="row" $align="center" $gap={0} style={{ justifyContent: 'center' }}>
                <ActionsMenu actions={createMentionActions(item, index)} size="medium" placement="right" />
                {showHistory && (
                  <HistoryPopover
                    companyID={item.companyID}
                    mentionID={item.postID}
                    brandInternalID={item.brandInternalID}
                    placement="right"
                    triggerIcon={<Button.Icon value={History} color={Tokens.ColorUIDanger} />}
                  />
                )}
              </Flex>
            </Flex>
          )
        },
      },
    ],
    [images, BatchActionsHeader, createMentionActions, expandedId, setMentionReviewed, isReviewPending],
  )

  return (
    <Layout.Root>
      <MentionsContainer>
        <div
          style={{
            width: '100%',
            minWidth: '1080px',
            margin: '0 auto',
            boxSizing: 'border-box',
          }}>
          <PageTitle
            title="Análise de Menções"
            subtitle="Gerencie e visualize as menções da empresa"
            icon={Comments}
            backToName={company?.name}
            onBackClick={() => navigate(Routes.CompanyDetails.path, companyID!)}
          />
        </div>
        <Filters />

        <MentionsTabsContainer>
          <MentionsTab type="button" $active={activeTab === 'data'} onClick={() => setActiveTab('data')}>
            Dados
          </MentionsTab>
          <MentionsTab type="button" $active={activeTab === 'dashboard'} onClick={() => setActiveTab('dashboard')}>
            Dashboard
          </MentionsTab>
        </MentionsTabsContainer>

        {activeTab === 'dashboard' && <Dashboard />}

        {activeTab === 'data' && (
          <>
            <Divider.Root>
              <Divider.Title value="Tabela de Menções" />
              <Divider.Spacer />
              <Divider.Subtitle value={`${mentions?.total ?? 0} Menções`} />
            </Divider.Root>

            {(mentionsIdList?.length ?? 0) > 0 && (
              <Auth.ProtectedComponent
                requiredRoles={[UserResponseRole.Admin, UserResponseRole.AdminMaster, UserResponseRole.Leadership]}>
                <Button.Root
                  $variant="danger"
                  onClick={handleDeleteAll}
                  style={{ width: '250px', flexShrink: 0 }}
                  $loading={isDeletingBatch}>
                  <Button.Text value="Deletar todas as menções" />
                </Button.Root>
              </Auth.ProtectedComponent>
            )}

            {isLoadingMentions ?
              <SkeletonLoader />
            : <FlexTable
                data={currMentions}
                columns={columns}
                totalPages={mentions?.pages}
                maxHeight="70vh"
                rowHeight="auto"
                renderRowMetrics={renderMentionMetrics}
                renderRowTags={renderMentionThemes}
                enableSelection={true}
                selectedItems={selectedMentionIds}
                onItemSelect={(mention) => toggleMentionSelection(mention.postID, mention.brandID)}
                selectAllChecked={selectAllVisible}
                onSelectAll={toggleSelectAllVisible}
                getItemId={(mention) => `${mention.postID}_${mention.brandID}`}
                onSortChange={handleSortChange}
                pageSizeOptions={[50, 100, 150, 200]}
                onPageSizeChange={handlePageSizeChange}
                currentPageSize={currentPageSize}
                currentSort={currentSort}
                sortOptions={SortOptions.mentions}
                enableSorting={true}
              />
            }
          </>
        )}
        {mentionModalType === 'edit' && <EditModal />}
        {mentionModalType === 'detail' && <MentionDetailModal />}
        {showBatchEditModal && <BatchEditModal onClose={() => setShowBatchEditModal(false)} />}
        {showLinkBrandsModal && <LinkBrandsModal onClose={() => setShowLinkBrandsModal(false)} />}
      </MentionsContainer>
    </Layout.Root>
  )
}
